import * as JSONMapper from "json-mapper";
import {JSONMapped, jsonProperty} from "json-mapper-reflect";
import * as moment from "moment";
import {contextPath} from "site";
import {AvdbTypeIssue} from "./avdb-type-issue";

export class AvdbType extends JSONMapped() {
	@jsonProperty()
	id: number;

	@jsonProperty()
	name: string;

	constructor(data?: Partial<AvdbType>) {
		super();
		Object.assign(this, data);
	}

	equals(other: AvdbType) {
		return this.id === other.id;
	}

	get displayOrder(): number {
		return AvdbType.displayOrder(this.id);
	}

	get logoURL(): string {
		const logoFilename = logoFilenames.get(this.id);
		if (logoFilename == null)
			return null;
		else
			return `${contextPath}/img/databases/${logoFilename}`;
	}

	get description(): string {
		return typeDescriptions.get(this.id);
	}

	get purchasableOnlyForAnnualSubscription(): boolean {
		return this.id === 12 // Map Enhancements
			|| this.id === 3 // Terrain
			|| this.id === 7; // Basemap
	}

	static displayOrder(avdbTypeID: number): number {
		return displayOrder.indexOf(avdbTypeID);
	}
}


export class AvdbTypeWithIssues extends AvdbType {
	@jsonProperty()
	daysPerCycle: number;

	@jsonProperty()
	leadTime: number;

	@jsonProperty()
	externalProvider: boolean;

	@jsonProperty(JSONMapper.arrayOfClass(AvdbTypeIssue))
	issues: Array<AvdbTypeIssue>;

	constructor(data?: Partial<AvdbTypeWithIssues>) {
		super();
		Object.assign(this, data);
	}

	get upcomingIssues() {
		let cutoffDate = moment().add({years: 2});
		return this.issues
			.filter(i => i.availableAt != null && i.availableAt.isBefore(cutoffDate));
	}
}


const displayOrder: Array<number> = [
	1,  // Navigation Data
	5,  // FliteCharts
	9,  // IFR/VFR Charts
	10, // Frequency Data
	11, // D2 Pilot Watch
	6,  // Airport Directory
	2,  // Obstacles
	4,  // SafeTaxi
	8,  // ChartView Key
	7,  // Basemap
	12, // Map Enhancements
	3,  // Terrain
];

// TODO: These should come from the database
const logoFilenames: Map<number, string> = new Map([
	[1,  "type-logo-navigation-data.svg"],   // Navigation Data
	[5,  "type-logo-flitecharts.svg"],       // FliteCharts
	[9,  "type-logo-ifr-vfr-charts.svg"],    // IFR/VFR Charts
	[10, "type-logo-frequency-data.svg"],    // Frequency Data
	[11, "type-logo-d2-pilot-watch.svg"],    // D2 Pilot Watch
	[6,  "type-logo-airport-directory.svg"], // Airport Directory
	[2,  "type-logo-obstacles.svg"],         // Obstacles
	[4,  "type-logo-safetaxi.svg"],          // SafeTaxi
	[8,  "type-logo-chartview-key.svg"],     // ChartView Key
	[7,  "type-logo-basemap.svg"],           // Basemap
	[12, "type-logo-map-enhancements.svg"],  // Map Enhancements
	[3,  "type-logo-terrain.svg"],           // Terrain
]);

// TODO: These should come from the database
const typeDescriptions: Map<number, string> = new Map([
	[1,  "The Navigation database contains airport, navaid, waypoint, frequency, airway, airspace, and terminal procedure information."], // Navigation Data
	[5,  "The FliteCharts database contains digital depictions of terminal procedure charts, including departure, arrival, and approach procedures. For certified devices, FliteCharts will no longer function 180 days after the expiration date."], // FliteCharts
	[9,  "The IFR/VFR Charts database contains digital depictions of VFR and IFR (low and high) enroute charts."], // IFR/VFR Charts
	[10, "The Frequency database contains communication and navigation frequency information associated with airports and NAVAIDs, such as tower, ground, ATIS/ASOS, approach, ILS, VOR, and FSS frequencies."], // Frequency Data
	[11, "The D2 Pilot Watch database contains airport locations to support Nearest and Direct-To functions of the watch. It is updated periodically and has no expiration date."], // D2 Pilot Watch
	[6,  "The Airport Directory database contains information related to airports and FBOs, including fuel prices (select systems only), as well as surrounding restaurant and rental car options."], // Airport Directory
	[2,  "The Obstacles database contains man-made structures, such as radio towers, with a height of 200 feet AGL and higher (shorter obstacles in helicopter version, when available)."], // Obstacles
	[4,  "The SafeTaxi database contains detailed airport taxiway diagrams for select airports. These diagrams aid in following ground control instructions by displaying the aircraft position on the moving map in relation to taxiways, ramps, runways, terminals."], // SafeTaxi
	[8,  "The ChartView Key allows your device to take advantage of Jeppesen-style instrument approach plates and airport surface charts by unlocking the installed ChartView software."], // ChartView Key
	[7,  "The Basemap database contains oceans, rivers, lakes, cities, highways, and political boundaries. It is updated periodically and has no expiration date."], // Basemap
	[12, ""], // Map Enhancements
	[3,  "The Terrain database contains topographic elevation data. It is updated periodically and has no expiration date. Terrain resolution of at least 9AS is required for SVT functionality on applicable systems."], // Terrain
]);
